import { useEffect, useRef, useState } from "react";
import { Loader2, Camera } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

/**
 * Scrub-to-frame thumbnail picker for showcase videos. Renders the video with a timeline
 * slider; "Use this frame" captures the exact current frame to a JPEG blob (client-side
 * canvas — the video file is never re-uploaded) and hands it to onCapture(blob, seconds).
 */
export const ThumbnailPicker = ({ videoUrl, onCapture, busy = false, testid = "thumb-picker" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const seek = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    setTime(t);
  };

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    // cap capture at 900px tall — plenty for a card thumbnail, keeps JPEGs small
    const scale = Math.min(1, 900 / v.videoHeight);
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (!blob) return;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      onCapture(new File([blob], "frame.jpg", { type: "image/jpeg" }), v.currentTime);
    }, "image/jpeg", 0.85);
  };

  return (
    <div data-testid={testid} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Choose a frame from the video</p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <video
          ref={videoRef}
          data-testid={`${testid}-video`}
          src={videoUrl}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => { setDuration(e.target.duration || 0); setReady(true); }}
          onTimeUpdate={(e) => setTime(e.target.currentTime)}
          className="max-h-44 w-full rounded-xl bg-black object-contain sm:w-2/3"
        />
        <div className="flex w-full flex-col items-center sm:w-1/3">
          <div className="flex w-full flex-1 items-center justify-center rounded-xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-subtle)] p-2">
            {previewUrl ? (
              <img data-testid={`${testid}-frame-preview`} src={previewUrl} alt="Selected frame" className="max-h-36 rounded-lg object-contain" />
            ) : (
              <span data-testid={`${testid}-frame-empty`} className="px-3 text-center text-[10px] font-semibold text-[var(--pv-text-4)]">
                {ready ? "Scrub, then capture — the picked frame previews here" : "Loading video…"}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <span data-testid={`${testid}-time`} className="w-16 shrink-0 font-mono-label text-[11px] font-bold text-cyan-300">{fmt(time)}</span>
        <input
          data-testid={`${testid}-slider`}
          type="range"
          min={0}
          max={duration || 0}
          step={0.04}
          value={time}
          onChange={(e) => seek(parseFloat(e.target.value))}
          disabled={!ready}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--pv-border)] accent-cyan-500 disabled:opacity-40"
        />
        <span className="w-16 shrink-0 text-right font-mono-label text-[11px] font-bold text-[var(--pv-text-4)]">{fmt(duration)}</span>
      </div>
      <button
        type="button"
        data-testid={`${testid}-capture-btn`}
        onClick={capture}
        disabled={!ready || busy}
        className="mt-3 flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-[11px] font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
        Use current frame as thumbnail
      </button>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
